import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';

// ── Colores por tipo de evento ────────────────────────────────
const COLORS = {
  individual: { bg: '#e8f4fd', border: '#5ba4cf', text: '#1a6193' },
  group:      { bg: '#e6f4ec', border: '#7aab8a', text: '#2d5a27' },
  school:     { bg: '#fef3e2', border: '#e8a838', text: '#8a5a00' },
  default:    { bg: '#f0ede8', border: '#a89880', text: '#5a4a38' },
};

function getEventColor(ev) {
  if (ev.category === 'school') return COLORS.school;
  if (ev.registrationType === 'individual') return COLORS.individual;
  if (ev.registrationType === 'group') return COLORS.group;
  return COLORS.default;
}

// ── Helpers de calendario ─────────────────────────────────────
function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}
function getFirstDayOfMonth(year, month) {
  // Monday = 0
  return (new Date(year, month, 1).getDay() + 6) % 7;
}

const MONTH_NAMES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const DAY_NAMES   = ['L','M','X','J','V','S','D'];

// ── Helper: agregar alergias normalizadas a partir de bookings ────────────
function aggregateAllergies(bookings) {
  const byTag = {};
  let allergicCount = 0;
  for (const b of bookings) {
    for (const m of (b.members || [])) {
      const tags = m.alergiasNormalizadas || [];
      if (tags.length) {
        allergicCount++;
        for (const t of tags) byTag[t] = (byTag[t] || 0) + 1;
      }
    }
  }
  return {
    allergicCount,
    uniqueAllergiesCount: Object.keys(byTag).length,
    byTag,
  };
}

// ── Panel agregado: alérgicos · alergias distintas · breakdown ────────────
function AllergyPanel({ stats, color }) {
  if (!stats || stats.allergicCount === 0) return null;
  const entries = Object.entries(stats.byTag).sort((a, b) => b[1] - a[1]);
  return (
    <div style={{ padding: '.7rem .9rem', borderTop: `1px solid ${color.border}`, background: 'rgba(232,168,56,.06)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '.9rem', marginBottom: '.45rem' }}>
        <span style={{ fontSize: '.62rem', fontWeight: 700, letterSpacing: '.12em', color: 'var(--mid)' }}>ALERGIAS / DIETAS</span>
        <span style={{ fontSize: '.82rem' }}>
          <strong style={{ color: 'var(--danger)' }}>{stats.allergicCount}</strong>
          <span style={{ color: 'var(--mid)' }}> alérgicos · </span>
          <strong>{stats.uniqueAllergiesCount}</strong>
          <span style={{ color: 'var(--mid)' }}> distintas</span>
        </span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.35rem' }}>
        {entries.map(([tag, count]) => (
          <span key={tag} style={{
            display: 'inline-flex', alignItems: 'center', gap: '.3rem',
            padding: '.18rem .55rem', borderRadius: '12px',
            background: 'rgba(232,168,56,.18)', border: '1px solid rgba(232,168,56,.4)',
            fontSize: '.78rem', color: '#5a4a38',
          }}>
            {tag}
            <strong style={{ fontSize: '.72rem' }}>{count}</strong>
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Modal: detalle de reserva ─────────────────────────────────
function DetailModal({ booking, event, onClose, onCancel, onRefund, onMarkPaid, onChangePlan }) {
  const [refunding, setRefunding] = useState(null); // planId being refunded
  const [refundErr, setRefundErr] = useState('');
  const [markingPaid, setMarkingPaid] = useState(null); // planId being marked
  const [editingPlan, setEditingPlan] = useState(false);
  const [newPlan, setNewPlan] = useState(booking.selectedPlan || 'single');
  const [changingPlan, setChangingPlan] = useState(false);
  const [planErr, setPlanErr] = useState('');

  const handleRefund = async (planId) => {
    const label = planId === 'all' ? 'todos los pagos' : `el pago "${planId}"`;
    if (!window.confirm(`¿Confirmar reembolso de ${label}? Esta acción no se puede deshacer.`)) return;
    setRefunding(planId);
    setRefundErr('');
    try {
      await onRefund(booking.id || booking.bookingId, planId);
      onClose();
    } catch (e) {
      setRefundErr(e.message);
    } finally {
      setRefunding(null);
    }
  };

  const handleMarkPaid = async (planId) => {
    const label = planId || 'este pago';
    if (!window.confirm(`¿Marcar "${label}" como pagado? Esta acción es manual y no procesa ningún cobro.`)) return;
    setMarkingPaid(planId);
    setRefundErr('');
    try {
      await onMarkPaid(booking.id || booking.bookingId, planId);
    } catch (e) {
      setRefundErr(e.message);
    } finally {
      setMarkingPaid(null);
    }
  };

  const payments = booking.payments || [];
  const paidPayments = payments.filter(p => p.status === 'paid' && p.stripeId);

  // Opciones de pago disponibles: se toman del evento si hay info; si no,
  // caemos a las que se copiaron al crear la reserva.
  const srcAllowSingle = (event && event.allowSinglePayment !== undefined)
    ? event.allowSinglePayment !== false
    : booking.allowSinglePayment !== false;
  const srcPaymentOptions = (event && Array.isArray(event.paymentOptions) && event.paymentOptions.length > 0)
    ? event.paymentOptions
    : (Array.isArray(booking.paymentOptions) ? booking.paymentOptions : []);

  const planOptionsDisponibles = [
    ...(srcAllowSingle ? [{ id: 'single', label: 'Pago unico' }] : []),
    ...srcPaymentOptions.map(o => ({ id: o.id, label: o.label || o.id })),
  ];

  const requiresPayment = !!booking.requiresPayment;
  const hayPagosEfectuados = payments.some(p => p.status === 'paid' || p.status === 'refunded') || (booking.totalPaid || 0) > 0;
  const puedeCambiarPlan = requiresPayment && !hayPagosEfectuados && planOptionsDisponibles.length > 1 && !!onChangePlan;

  const currentPlanId = booking.selectedPlan || 'single';
  const currentPlanLabel = currentPlanId === 'single'
    ? 'Pago unico'
    : (booking.selectedOptionLabel || planOptionsDisponibles.find(o => o.id === currentPlanId)?.label || currentPlanId);

  const handleSavePlan = async () => {
    if (newPlan === currentPlanId) { setEditingPlan(false); return; }
    if (!window.confirm(`Cambiar la forma de pago a "${planOptionsDisponibles.find(o => o.id === newPlan)?.label || newPlan}"? Se regenerara el desglose de pagos.`)) return;
    setChangingPlan(true);
    setPlanErr('');
    try {
      await onChangePlan(booking.id || booking.bookingId, newPlan);
      setEditingPlan(false);
      onClose();
    } catch (e) {
      setPlanErr(e.message);
    } finally {
      setChangingPlan(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-lg">
        <div className="modal-header">
          <div>
            <div className="section-label">Datos del formulario</div>
            <h2>{booking.familyName} · {booking.eventName || booking.eventId}</h2>
            {booking.checkInDate && booking.checkOutDate && (
              <p style={{ fontSize: '.85rem', color: 'var(--sage)', marginTop: '.2rem' }}>
                🗓 Llegada {new Date(booking.checkInDate).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
                {' → '}salida {new Date(booking.checkOutDate).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
              </p>
            )}
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        {(booking.members || []).map((m, i) => (
          <div key={i} style={{ background: 'rgba(122,171,138,.07)', border: '1px solid rgba(122,171,138,.15)', borderRadius: 'var(--radius)', overflow: 'hidden', marginBottom: '.8rem' }}>
            <div style={{ padding: '.5rem .9rem', background: 'var(--fern)', color: '#fff', fontFamily: "'Fraunces',serif", fontSize: '.62rem', fontWeight: 700, letterSpacing: '.1em', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{m.firstName || m.name} {m.lastName1} {m.lastName2}</span>
              {m.personaId && (
                <span style={{ fontSize: '.55rem', fontFamily: "'Inter',sans-serif", fontWeight: 600, background: 'rgba(255,255,255,.2)', padding: '.15rem .4rem', borderRadius: '10px', letterSpacing: '.05em' }}>
                  Persona vinculada
                </span>
              )}
            </div>
            {[
              ['Email', m.email],
              ['Teléfono', m.phoneExt && m.phone ? `${m.phoneExt} ${m.phone}` : m.phone],
              ['Año nac.', m.birthYear], ['Sexo', m.sex], ['País', m.country],
              ['Municipio / Provincia', m.municipality && m.province ? `${m.municipality} / ${m.province}` : m.municipality || m.province],
              ['Tipo doc.', m.docType?.toUpperCase()], ['Nº documento', m.docNum], ['Nº soporte', m.docSupport],
              ['Fecha emisión', m.docEmission], ['Tutor legal', m.tutorName],
              ['Alergias / Dieta', m.alergias],
              ['Medicación', m.medicacionActual],
              ['Notas de los padres', m.dietaryNotes],
            ].filter(([, v]) => v).map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '.55rem .9rem', borderBottom: '1px solid rgba(45,90,39,.07)', fontSize: '.88rem', gap: '.7rem' }}>
                <span style={{ color: 'var(--mid)', fontSize: '.8rem', flexShrink: 0 }}>{k}</span>
                <span style={{ fontFamily: ['Nº documento','Nº soporte'].includes(k) ? "'JetBrains Mono',monospace" : undefined, textAlign: 'right' }}>{v}</span>
              </div>
            ))}
            {(m.alergiasNormalizadas || []).length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.3rem', padding: '.4rem .9rem .6rem' }}>
                {m.alergiasNormalizadas.map((tag) => (
                  <span key={tag} style={{
                    padding: '.15rem .5rem', borderRadius: '10px',
                    background: 'rgba(232,168,56,.18)', border: '1px solid rgba(232,168,56,.4)',
                    fontSize: '.72rem', color: '#5a4a38',
                  }}>{tag}</span>
                ))}
              </div>
            )}
          </div>
        ))}

        {requiresPayment && (
          <div style={{ marginBottom: '1rem' }}>
            <div className="section-label" style={{ marginBottom: '.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Forma de pago</span>
              {puedeCambiarPlan && !editingPlan && (
                <button
                  className="btn btn-ghost btn-sm"
                  style={{ fontSize: '.72rem', padding: '.15rem .5rem' }}
                  onClick={() => { setNewPlan(currentPlanId); setEditingPlan(true); setPlanErr(''); }}
                >
                  ⇄ Cambiar
                </button>
              )}
            </div>
            {!editingPlan ? (
              <div style={{
                padding: '.6rem .9rem',
                border: '1px solid rgba(122,171,138,.2)',
                borderRadius: 'var(--radius)',
                background: 'rgba(122,171,138,.04)',
                fontSize: '.88rem',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <span style={{ fontWeight: 600 }}>{currentPlanLabel}</span>
                {hayPagosEfectuados && (
                  <span style={{ fontSize: '.7rem', color: 'var(--mid)', fontStyle: 'italic' }}>
                    Bloqueado — hay pagos efectuados
                  </span>
                )}
                {!hayPagosEfectuados && planOptionsDisponibles.length <= 1 && (
                  <span style={{ fontSize: '.7rem', color: 'var(--mid)', fontStyle: 'italic' }}>
                    Solo hay una opcion disponible para este evento
                  </span>
                )}
              </div>
            ) : (
              <div style={{
                padding: '.6rem .9rem',
                border: '1px solid rgba(91,164,207,.35)',
                borderRadius: 'var(--radius)',
                background: 'rgba(91,164,207,.06)',
              }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '.4rem' }}>
                  {planOptionsDisponibles.map(opt => (
                    <label key={opt.id} style={{ display: 'flex', alignItems: 'center', gap: '.55rem', fontSize: '.88rem', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="nuevoPlan"
                        value={opt.id}
                        checked={newPlan === opt.id}
                        onChange={() => setNewPlan(opt.id)}
                      />
                      <span style={{ fontWeight: newPlan === opt.id ? 700 : 400 }}>{opt.label}</span>
                      {opt.id === currentPlanId && (
                        <span style={{ fontSize: '.65rem', color: 'var(--mid)', fontStyle: 'italic' }}>(actual)</span>
                      )}
                    </label>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '.4rem', justifyContent: 'flex-end', marginTop: '.6rem' }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => { setEditingPlan(false); setPlanErr(''); }} disabled={changingPlan}>
                    Cancelar
                  </button>
                  <button className="btn btn-primary btn-sm" onClick={handleSavePlan} disabled={changingPlan || newPlan === currentPlanId}>
                    {changingPlan ? 'Guardando…' : 'Guardar cambio'}
                  </button>
                </div>
                {planErr && <div className="form-error" style={{ marginTop: '.4rem' }}>{planErr}</div>}
              </div>
            )}
          </div>
        )}

        {payments.length > 0 && (
          <div style={{ marginBottom: '1rem' }}>
            <div className="section-label" style={{ marginBottom: '.5rem' }}>Pagos</div>
            <div style={{ border: '1px solid rgba(122,171,138,.2)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
              {payments.map((p, i) => {
                const isOverdue = p.status === 'pending' && p.dueDate && new Date(p.dueDate) < new Date();
                const statusColor = p.status === 'paid' ? 'var(--fern)' : p.status === 'refunded' ? 'var(--amber)' : isOverdue ? '#c0392b' : 'var(--mid)';
                const statusLabel = p.status === 'paid' ? 'Pagado' : p.status === 'refunded' ? 'Reembolsado' : p.status === 'pending' ? (isOverdue ? 'Vencido' : 'Pendiente') : p.status;
                return (
                  <div key={p.planId || i} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '.55rem .9rem',
                    borderBottom: i < payments.length - 1 ? '1px solid rgba(122,171,138,.1)' : 'none', fontSize: '.88rem',
                    background: isOverdue ? 'rgba(192,57,43,.06)' : 'transparent',
                    borderLeft: isOverdue ? '3px solid #c0392b' : '3px solid transparent',
                  }}>
                    <div>
                      <span>{p.label || p.planId}</span>
                      {p.manuallyMarked && (
                        <span style={{ fontSize: '.7rem', color: 'var(--mid)', fontStyle: 'italic', marginLeft: '.5rem' }}>(marcado manualmente)</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '.8rem' }}>
                      <span style={{ fontWeight: 600 }}>{p.amount != null ? `${p.amount.toFixed(2)} €` : '—'}</span>
                      <span style={{ fontSize: '.75rem', color: statusColor, fontWeight: 600 }}>{statusLabel}</span>
                      {p.status === 'pending' && (
                        <button
                          className="btn btn-sm"
                          style={{ fontSize: '.75rem', padding: '.2rem .5rem', background: 'var(--fern)', color: '#fff', border: 'none' }}
                          disabled={markingPaid !== null || refunding !== null}
                          onClick={() => handleMarkPaid(p.planId)}
                        >
                          {markingPaid === p.planId ? 'Procesando…' : 'Marcar como pagado'}
                        </button>
                      )}
                      {p.status === 'paid' && p.stripeId && (
                        <button
                          className="btn btn-ghost btn-sm"
                          style={{ fontSize: '.75rem', padding: '.2rem .5rem' }}
                          disabled={refunding !== null || markingPaid !== null}
                          onClick={() => handleRefund(p.planId)}
                        >
                          {refunding === p.planId ? 'Procesando…' : 'Reembolsar'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {paidPayments.length > 1 && (
              <div style={{ marginTop: '.5rem', textAlign: 'right' }}>
                <button
                  className="btn btn-ghost btn-sm"
                  style={{ fontSize: '.8rem' }}
                  disabled={refunding !== null}
                  onClick={() => handleRefund('all')}
                >
                  {refunding === 'all' ? 'Procesando…' : 'Reembolsar todo'}
                </button>
              </div>
            )}
            {refundErr && <div className="form-error" style={{ marginTop: '.4rem' }}>{refundErr}</div>}
          </div>
        )}

        <div style={{ display: 'flex', gap: '.6rem', justifyContent: 'flex-end' }}>
          {booking.status === 'confirmed' && (
            <button className="btn btn-danger btn-sm" onClick={() => onCancel(booking.id || booking.bookingId)}>Cancelar reserva</button>
          )}
          <button className="btn btn-ghost" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

// ── Modal: detalle de un día en el calendario ─────────────────
function CalendarEventModal({ events, bookings, day, year, month, onClose, onAddBooking, onSetExternalCount }) {
  const dateStr = `${year}-${String(month + 1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;

  const eventsOnDay = events.filter(ev => {
    if (!ev.date) return false;
    const startDate = new Date(ev.date);
    const endDate = ev.dateEnd ? new Date(ev.dateEnd) : startDate;
    const start = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const end   = new Date(endDate.getFullYear(),   endDate.getMonth(),   endDate.getDate());
    const d     = new Date(
      parseInt(dateStr.slice(0,4)),
      parseInt(dateStr.slice(5,7)) - 1,
      parseInt(dateStr.slice(8,10))
    );
    return d >= start && d <= end;
  });

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <div>
            <div className="section-label">Calendario</div>
            <h2>{day} de {MONTH_NAMES[month]} {year}</h2>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {eventsOnDay.length === 0 ? (
          <p style={{ color: 'var(--mid)', fontStyle: 'italic' }}>No hay eventos este día.</p>
        ) : eventsOnDay.map(ev => {
          const evBookings   = bookings.filter(b => b.eventId === ev.id && b.status !== 'cancelled');
          const withData     = evBookings.reduce((s, b) => s + (b.totalMembers || 0), 0);
          const externalCount = ev.externalCount || 0;
          const total        = withData + externalCount;
          const available    = Math.max(0, ev.maxCapacity - total);
          const col          = getEventColor(ev);
          const allergyStats = aggregateAllergies(evBookings);

          return (
            <div key={ev.id} style={{ border: `1px solid ${col.border}`, borderRadius: 'var(--radius)', overflow: 'hidden', marginBottom: '1rem' }}>
              <div style={{ padding: '.5rem .9rem', background: col.bg, borderBottom: `1px solid ${col.border}` }}>
                <span style={{ fontWeight: 700, color: col.text }}>{ev.name}</span>
                {ev.category === 'school' && <span style={{ marginLeft: '.5rem', fontSize: '.75rem', color: col.text }}>🎒 Colegio</span>}
              </div>
              <div style={{ padding: '.7rem .9rem', display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '.5rem', textAlign: 'center' }}>
                {[
                  ['Con datos', withData, 'var(--fern)'],
                  ['Sin datos', externalCount, 'var(--amber)'],
                  ['Total', total, 'var(--ink)'],
                  ['Libres', available, available === 0 ? 'var(--danger)' : 'var(--sage)'],
                ].map(([label, val, color]) => (
                  <div key={label}>
                    <div style={{ fontSize: '1.4rem', fontWeight: 800, color }}>{val}</div>
                    <div style={{ fontSize: '.72rem', color: 'var(--mid)' }}>{label}</div>
                  </div>
                ))}
              </div>
              <AllergyPanel stats={allergyStats} color={col} />
              <div style={{ padding: '.5rem .9rem', display: 'flex', gap: '.5rem', borderTop: `1px solid ${col.border}` }}>
                <button className="btn btn-secondary btn-sm" onClick={() => { onClose(); onAddBooking(ev); }}>
                  + Inscripción manual
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => { onClose(); onSetExternalCount(ev); }}>
                  ✎ Sin datos ({externalCount})
                </button>
              </div>
            </div>
          );
        })}
        <div style={{ textAlign: 'right' }}>
          <button className="btn btn-ghost" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

// ── Modal: ajustar conteo externo ─────────────────────────────
function ExternalCountModal({ event, token, onClose, onSaved }) {
  const [value, setValue] = useState(event.externalCount ?? 0);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const save = async () => {
    const num = parseInt(value, 10);
    if (isNaN(num) || num < 0) { setErr('Introduce un número válido >= 0.'); return; }
    setSaving(true);
    try {
      await api.setExternalCount(token, event.id, num);
      onSaved(event.id, num);
      onClose();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <div>
            <div className="section-label">Reservas sin datos</div>
            <h2>{event.name}</h2>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <p style={{ fontSize: '.9rem', color: 'var(--mid)', marginBottom: '1rem' }}>
          Número de personas inscritas en este evento <strong>sin datos en el sistema</strong> (colegios sin acceso, reservas externas, etc.). Se suma al aforo ocupado.
        </p>
        <div className="form-group">
          <label className="form-label">Inscritos sin datos</label>
          <input
            className="form-input"
            type="number"
            min="0"
            value={value}
            onChange={e => { setValue(e.target.value); setErr(''); }}
          />
          {err && <div className="form-error">{err}</div>}
        </div>
        <div style={{ display: 'flex', gap: '.6rem', justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancelar</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modal: inscripción manual ─────────────────────────────────
function AddBookingModal({ event, token, onClose, onCreated }) {
  const [familyName, setFamilyName] = useState('');
  const [email, setEmail] = useState('');
  const [numMembers, setNumMembers] = useState(1);
  const [members, setMembers] = useState([{ name: '', age: '', role: 'adult' }]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const updateCount = (n) => {
    const count = Math.max(1, Math.min(30, parseInt(n, 10) || 1));
    setNumMembers(count);
    setMembers(prev => {
      const next = [...prev];
      while (next.length < count) next.push({ name: '', age: '', role: 'adult' });
      return next.slice(0, count);
    });
  };

  const updateMember = (i, field, val) => {
    setMembers(prev => prev.map((m, idx) => idx === i ? { ...m, [field]: val } : m));
  };

  const save = async () => {
    if (!familyName.trim()) { setErr('El nombre de familia es obligatorio.'); return; }
    if (!email.trim()) { setErr('El email de contacto es obligatorio.'); return; }
    for (let i = 0; i < members.length; i++) {
      if (!members[i].name.trim()) { setErr(`Miembro ${i+1}: nombre obligatorio.`); return; }
      const age = parseInt(members[i].age, 10);
      if (isNaN(age) || age < 0 || age > 120) { setErr(`Miembro ${i+1}: edad inválida.`); return; }
    }

    const cleanMembers = members.map(m => ({
      name:  m.name.trim(),
      firstName: m.name.trim(),
      age:   parseInt(m.age, 10),
      role:  m.role,
    }));

    setSaving(true);
    setErr('');
    try {
      await api.createAdminBooking(token, {
        eventId: event.id,
        members: cleanMembers,
        familyName: familyName.trim(),
        contactEmail: email.trim(),
      });
      onCreated();
      onClose();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  const available = Math.max(0, event.maxCapacity - (event.currentCount || 0) - (event.externalCount || 0));

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-lg">
        <div className="modal-header">
          <div>
            <div className="section-label">Inscripción manual</div>
            <h2>{event.name}</h2>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div style={{ background: 'rgba(122,171,138,.07)', border: '1px solid rgba(122,171,138,.15)', borderRadius: 'var(--radius)', padding: '.7rem .9rem', marginBottom: '1rem', fontSize: '.85rem', color: 'var(--mid)' }}>
          Plazas disponibles: <strong style={{ color: available === 0 ? 'var(--danger)' : 'var(--fern)' }}>{available}</strong>
          {' · '}Sin pago — se confirma directamente.
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
          <div className="form-group">
            <label className="form-label">Familia / Grupo</label>
            <input className="form-input" value={familyName} onChange={e => setFamilyName(e.target.value)} placeholder="Familia García" />
          </div>
          <div className="form-group">
            <label className="form-label">Email de contacto</label>
            <input className="form-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="familia@ejemplo.com" />
          </div>
        </div>

        <div className="form-group" style={{ marginBottom: '1rem' }}>
          <label className="form-label">Número de miembros</label>
          <input className="form-input" type="number" min="1" max="30" style={{ width: '120px' }} value={numMembers} onChange={e => updateCount(e.target.value)} />
        </div>

        {members.map((m, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '.6rem', alignItems: 'end', marginBottom: '.5rem' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label" style={{ fontSize: '.75rem' }}>Nombre miembro {i + 1}</label>
              <input className="form-input" value={m.name} onChange={e => updateMember(i, 'name', e.target.value)} placeholder="Nombre" />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label" style={{ fontSize: '.75rem' }}>Edad</label>
              <input className="form-input" type="number" min="0" max="120" style={{ width: '80px' }} value={m.age} onChange={e => updateMember(i, 'age', e.target.value)} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label" style={{ fontSize: '.75rem' }}>Rol</label>
              <select className="form-select" value={m.role} onChange={e => updateMember(i, 'role', e.target.value)} style={{ minWidth: '100px' }}>
                <option value="adult">Adulto</option>
                <option value="child">Menor</option>
              </select>
            </div>
          </div>
        ))}

        {err && <div className="alert alert-error" style={{ marginTop: '.8rem' }}><span>⚠</span> {err}</div>}

        <div style={{ display: 'flex', gap: '.6rem', justifyContent: 'flex-end', marginTop: '1.2rem' }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancelar</button>
          <button className="btn btn-primary" onClick={save} disabled={saving || available === 0}>
            {saving ? 'Guardando…' : 'Confirmar inscripción'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── CSV export ────────────────────────────────────────────────
function exportBookingsCSV(bookings) {
  const headers = ['ID','Familia','Email','Evento','Tipo','Inscritos','Estado','Pago','Pagado (€)','Pendiente (€)','Creado'];
  const rows = bookings.map(b => [
    b.id || b.bookingId || '',
    b.familyName || '',
    b.contactEmail || '',
    b.eventName || b.eventId || '',
    b.registrationType === 'individual' ? 'Individual' : 'Grupal',
    b.totalMembers || b.members?.length || 1,
    b.status === 'confirmed' ? 'Confirmada' : b.status === 'pending_payment' ? 'Pdte. pago' : 'Cancelada',
    b.paymentStatus || '',
    b.totalPaid != null ? (b.totalPaid / 100).toFixed(2) : '',
    b.totalDue  != null ? (b.totalDue  / 100).toFixed(2) : '',
    b.createdAt ? new Date(b.createdAt._seconds ? b.createdAt._seconds * 1000 : b.createdAt).toLocaleDateString('es-ES') : '',
  ]);

  const csv = [headers, ...rows]
    .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\r\n');

  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `reservas_${new Date().toISOString().slice(0,10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Vista de lista ────────────────────────────────────────────
function hasOverdue(b) {
  if (b.hasOverduePayments) return true;
  return (b.payments || []).some(p => p.status === 'pending' && p.dueDate && new Date(p.dueDate) < new Date());
}

function getPaymentBadge(b) {
  if (hasOverdue(b)) return { label: 'Impago vencido', bg: '#c0392b', color: '#fff' };
  if (b.paymentStatus === 'completed') return { label: 'Pagado', bg: 'var(--fern)', color: '#fff' };
  if (b.paymentStatus === 'partial') return { label: 'Parcial', bg: '#e8a838', color: '#fff' };
  if (b.paymentStatus === 'deposit_paid') return { label: 'Señal pagada', bg: '#3498db', color: '#fff' };
  if (b.paymentStatus) return { label: b.paymentStatus, bg: 'var(--mid)', color: '#fff' };
  return null;
}

function ListView({ bookings, events, loading, error, onCancel, onRefund, onMarkPaid, onChangePlan }) {
  const [filter, setFilter]           = useState('all');
  const [eventFilter, setEventFilter] = useState('all');
  const [search, setSearch]           = useState('');
  const [detail, setDetail]           = useState(null);
  const [overdueFilter, setOverdueFilter] = useState(false);

  const filtered = bookings.filter(b => {
    if (filter !== 'all' && b.status !== filter) return false;
    if (eventFilter !== 'all' && b.eventId !== eventFilter) return false;
    if (overdueFilter && !hasOverdue(b)) return false;
    if (search) {
      const q = search.toLowerCase();
      return (b.familyName || '').toLowerCase().includes(q) ||
             (b.contactEmail || '').toLowerCase().includes(q) ||
             (b.eventName || b.eventId || '').toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <>
      <div style={{ display: 'flex', gap: '.6rem', alignItems: 'center', marginBottom: '1.2rem', flexWrap: 'wrap' }}>
        <input
          className="form-input"
          style={{ flex: 1, minWidth: '160px', maxWidth: '240px' }}
          placeholder="Buscar por familia o email…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          className="form-select"
          style={{ minWidth: '160px', maxWidth: '240px' }}
          value={eventFilter}
          onChange={e => setEventFilter(e.target.value)}
        >
          <option value="all">Todos los eventos</option>
          {(events || []).map(ev => (
            <option key={ev.id} value={ev.id}>{ev.name}</option>
          ))}
        </select>
        <div style={{ display: 'flex', gap: '.4rem', flexWrap: 'wrap' }}>
          {['all','confirmed','pending_payment','cancelled'].map(f => (
            <button key={f} className={`btn btn-sm ${filter === f ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setFilter(f)}>
              {f === 'all' ? 'Todas' : f === 'confirmed' ? 'Confirmadas' : f === 'pending_payment' ? 'Pendiente pago' : 'Canceladas'}
            </button>
          ))}
          <button
            className="btn btn-sm btn-ghost"
            onClick={() => exportBookingsCSV(filtered)}
            disabled={filtered.length === 0}
            title="Exportar reservas visibles como CSV"
          >
            ⬇ CSV ({filtered.length})
          </button>
          <button
            className={`btn btn-sm ${overdueFilter ? 'btn-primary' : 'btn-ghost'}`}
            style={overdueFilter ? { background: '#c0392b', borderColor: '#c0392b' } : { color: '#c0392b' }}
            onClick={() => setOverdueFilter(f => !f)}
          >
            Con impagos
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error"><span>⚠</span> {error}</div>}

      {loading ? (
        <div className="loading-screen" style={{ minHeight: 'auto', padding: '3rem' }}><div className="spinner spinner-lg" /></div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Nombre</th><th>Evento</th><th>Tipo</th><th>Inscritos</th><th>Estado</th><th>Pagos</th><th>Datos</th><th></th></tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan="8" style={{ textAlign: 'center', color: 'var(--mid)', fontStyle: 'italic', padding: '3rem' }}>No hay reservas.</td></tr>
                ) : filtered.map(b => (
                  <tr key={b.id || b.bookingId} style={{ opacity: b.status === 'cancelled' ? .55 : 1 }}>
                    <td>
                      <div style={{ fontWeight: 700 }}>{b.familyName || '—'}</div>
                      <div style={{ fontSize: '.78rem', color: 'var(--mid)' }}>{b.contactEmail}</div>
                      {b.adminCreated && <div style={{ fontSize: '.7rem', color: 'var(--amber)', fontWeight: 600 }}>✎ Manual</div>}
                    </td>
                    <td style={{ fontSize: '.88rem' }}>
                      {b.eventName || b.eventId}
                      {b.checkInDate && b.checkOutDate && (
                        <div style={{ fontSize: '.75rem', color: 'var(--sage)', marginTop: '.15rem' }}>
                          🗓 {new Date(b.checkInDate).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })} → {new Date(b.checkOutDate).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                        </div>
                      )}
                    </td>
                    <td><span className={`badge ${b.registrationType === 'individual' ? 'badge-indiv' : 'badge-group'}`}>{b.registrationType === 'individual' ? '👤 Individual' : '👨‍👩‍👧 Grupal'}</span></td>
                    <td><b>{b.totalMembers || b.members?.length || 1}</b></td>
                    <td>
                      <span className={`badge ${b.status === 'confirmed' ? 'badge-green' : b.status === 'pending_payment' ? 'badge-amber' : 'badge-danger'}`}>
                        {b.status === 'confirmed' ? 'Confirmada' : b.status === 'pending_payment' ? 'Pdte. pago' : 'Cancelada'}
                      </span>
                    </td>
                    <td>
                      {(() => {
                        const badge = getPaymentBadge(b);
                        if (!badge) return '—';
                        return (
                          <span style={{ display: 'inline-block', fontSize: '.72rem', fontWeight: 600, padding: '.15rem .5rem', borderRadius: '10px', background: badge.bg, color: badge.color, whiteSpace: 'nowrap' }}>
                            {badge.label}
                          </span>
                        );
                      })()}
                    </td>
                    <td><button className="btn btn-secondary btn-sm" onClick={() => setDetail(b)}>Ver 📋</button></td>
                    <td>{b.status !== 'cancelled' && <button className="btn btn-danger btn-sm" onClick={() => onCancel(b.id || b.bookingId)}>Cancelar</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {detail && (
        <DetailModal
          booking={detail}
          event={(events || []).find(ev => ev.id === detail.eventId) || null}
          onClose={() => setDetail(null)}
          onCancel={(id) => { onCancel(id); setDetail(null); }}
          onRefund={onRefund}
          onMarkPaid={onMarkPaid}
          onChangePlan={onChangePlan}
        />
      )}
    </>
  );
}

// ── Vista de calendario ───────────────────────────────────────
function CalendarView({ events, bookings, onAddBooking, onSetExternalCount }) {
  const now = new Date();
  const [year, setYear]   = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [calDetail, setCalDetail] = useState(null); // event object

  const prevMonth = () => {
    if (month === 0) { setYear(y => y - 1); setMonth(11); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setYear(y => y + 1); setMonth(0); }
    else setMonth(m => m + 1);
  };

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay    = getFirstDayOfMonth(year, month); // 0=Mon

  const getEventsForDay = useCallback((day) => {
    return events.filter(ev => {
      if (!ev.date) return false;
      const startDate = new Date(ev.date);
      const endDate = ev.dateEnd ? new Date(ev.dateEnd) : startDate;
      const start = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
      const end   = new Date(endDate.getFullYear(),   endDate.getMonth(),   endDate.getDate());
      const d     = new Date(year, month, day);
      return d >= start && d <= end;
    });
  }, [events, year, month]);

  const getTotalForDay = useCallback((day) => {
    const evs = getEventsForDay(day);
    return evs.reduce((sum, ev) => {
      const evBookings = bookings.filter(b => b.eventId === ev.id && b.status !== 'cancelled');
      return sum + evBookings.reduce((s, b) => s + (b.totalMembers || 0), 0) + (ev.externalCount || 0);
    }, 0);
  }, [getEventsForDay, bookings]);

  return (
    <div>
      {/* Nav */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.2rem' }}>
        <button className="btn btn-ghost btn-sm" onClick={prevMonth}>← Anterior</button>
        <h3 style={{ margin: 0, fontFamily: "'Fraunces',serif" }}>{MONTH_NAMES[month]} {year}</h3>
        <button className="btn btn-ghost btn-sm" onClick={nextMonth}>Siguiente →</button>
      </div>

      {/* Day headers */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', marginBottom: '2px' }}>
        {DAY_NAMES.map(d => (
          <div key={d} style={{ textAlign: 'center', fontSize: '.72rem', fontWeight: 700, color: 'var(--mid)', padding: '.3rem 0', letterSpacing: '.05em' }}>{d}</div>
        ))}
      </div>

      {/* Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', border: '1px solid rgba(122,171,138,.15)', borderRight: 'none', borderBottom: 'none' }}>
        {Array.from({ length: firstDay }).map((_, i) => (
          <div key={`empty-${i}`} style={{
            height: 110,
            borderRight: '1px solid rgba(122,171,138,.08)',
            borderBottom: '1px solid rgba(122,171,138,.08)',
          }} />
        ))}

        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const dayEvents = getEventsForDay(day);
          const total = getTotalForDay(day);
          const isToday = new Date().getDate() === day && new Date().getMonth() === month && new Date().getFullYear() === year;

          return (
            <div key={day} style={{
              height: 110,
              padding: '.3rem .4rem',
              borderRight: '1px solid rgba(122,171,138,.08)',
              borderBottom: '1px solid rgba(122,171,138,.08)',
              background: isToday ? 'rgba(122,171,138,.06)' : 'transparent',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '.2rem', flexShrink: 0 }}>
                <span style={{
                  fontSize: '.75rem', fontWeight: isToday ? 700 : 400,
                  color: isToday ? 'var(--sage)' : 'var(--mid)',
                  width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: '50%', background: isToday ? 'rgba(122,171,138,.2)' : 'transparent',
                  flexShrink: 0,
                }}>{day}</span>
                {total > 0 && (
                  <span style={{ fontSize: '.58rem', fontWeight: 700, color: 'var(--sage)', background: 'rgba(122,171,138,.15)', padding: '.1rem .3rem', borderRadius: 8, flexShrink: 0 }}>
                    {total}
                  </span>
                )}
              </div>
              <div style={{ overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '.15rem' }}>
                {dayEvents.map(ev => {
                  const c = getEventColor(ev);
                  const count = (ev.currentCount || 0) + (ev.externalCount || 0);
                  return (
                    <div key={ev.id}
                      onClick={() => setCalDetail(ev)}
                      style={{
                        background: c.bg,
                        border: `1px solid ${c.border}`,
                        borderRadius: 3,
                        padding: '.12rem .3rem',
                        cursor: 'pointer',
                        fontSize: '.6rem',
                        color: c.text,
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                      }}
                      title={`${ev.name} · ${count}/${ev.maxCapacity}`}
                    >
                      {ev.name} · {count}/{ev.maxCapacity}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem', flexWrap: 'wrap' }}>
        {Object.entries(COLORS).filter(([k]) => k !== 'default').map(([key, col]) => (
          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '.3rem', fontSize: '.78rem' }}>
            <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: col.bg, border: `1px solid ${col.border}` }} />
            <span style={{ color: 'var(--mid)' }}>{key === 'individual' ? 'Individual' : key === 'group' ? 'Grupal' : 'Colegio'}</span>
          </div>
        ))}
      </div>

      {calDetail && (
        <CalendarEventModal
          events={[calDetail]}
          bookings={bookings}
          day={new Date(calDetail.date).getDate()}
          year={new Date(calDetail.date).getFullYear()}
          month={new Date(calDetail.date).getMonth()}
          onClose={() => setCalDetail(null)}
          onAddBooking={(ev) => { setCalDetail(null); onAddBooking(ev); }}
          onSetExternalCount={(ev) => { setCalDetail(null); onSetExternalCount(ev); }}
        />
      )}
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────
export default function AdminBookings() {
  const { token } = useAuth();
  const [tab, setTab]           = useState('list');
  const [bookings, setBookings] = useState([]);
  const [events, setEvents]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');

  // Modal state
  const [addBookingEvent, setAddBookingEvent]         = useState(null);
  const [externalCountEvent, setExternalCountEvent]   = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.getAdminBookings(token),
      api.getAdminEvents(token),
    ])
      .then(([bd, ed]) => {
        setBookings(bd.bookings || []);
        setEvents(ed.events || []);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(load, [load]);

  const handleCancel = async (id) => {
    if (!window.confirm('¿Cancelar esta reserva?')) return;
    try {
      await api.cancelAdminBooking(token, id);
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleRefund = async (bookingId, planId) => {
    await api.refundBooking(token, bookingId, planId);
    load();
  };

  const handleMarkPaid = async (bookingId, planId) => {
    await api.markPaymentPaid(token, bookingId, planId);
    load();
  };

  const handleChangePaymentPlan = async (bookingId, selectedPlan) => {
    await api.changeBookingPaymentPlan(token, bookingId, selectedPlan);
    load();
  };

  const handleExternalCountSaved = (eventId, newCount) => {
    setEvents(prev => prev.map(ev => ev.id === eventId ? { ...ev, externalCount: newCount } : ev));
  };

  return (
    <div className="main-content page-enter">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '1.8rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <div className="section-label">Administración</div>
          <h1>Reservas</h1>
        </div>
        <div style={{ display: 'flex', gap: '.4rem' }}>
          <button className={`btn btn-sm ${tab === 'list' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setTab('list')}>📋 Lista</button>
          <button className={`btn btn-sm ${tab === 'calendar' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setTab('calendar')}>📅 Calendario</button>
        </div>
      </div>

      {tab === 'list' && (
        <ListView
          bookings={bookings}
          events={events}
          loading={loading}
          error={error}
          onCancel={handleCancel}
          onRefund={handleRefund}
          onMarkPaid={handleMarkPaid}
          onChangePlan={handleChangePaymentPlan}
        />
      )}

      {tab === 'calendar' && (
        <div className="card">
          <CalendarView
            events={events}
            bookings={bookings}
            onAddBooking={ev => setAddBookingEvent(ev)}
            onSetExternalCount={ev => setExternalCountEvent(ev)}
          />
        </div>
      )}

      {addBookingEvent && (
        <AddBookingModal
          event={addBookingEvent}
          token={token}
          onClose={() => setAddBookingEvent(null)}
          onCreated={load}
        />
      )}

      {externalCountEvent && (
        <ExternalCountModal
          event={externalCountEvent}
          token={token}
          onClose={() => setExternalCountEvent(null)}
          onSaved={handleExternalCountSaved}
        />
      )}
    </div>
  );
}
